import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import Layout from '../components/Layout';
import { Alert, Badge, Button, Card, EmptyState } from '../components/ui.jsx';
import {
  NEEDS_ATTENTION_FILTERS,
  buildNeedsAttentionQuery,
  getNeedsAttentionCounts,
  getNeedsAttentionEmptyState,
  getNeedsAttentionRow,
} from '../utils/needsAttentionUi.js';

const SUMMARY_CARDS = [
  { key: 'total', label: 'Total' },
  { key: 'failed_text', label: 'Text failed' },
  { key: 'missing_phone', label: 'Missing phone' },
  { key: 'reply_needs_scheduling', label: 'Needs interview' },
  { key: 'no_show_followup', label: 'No-show follow-up' },
  { key: 'stale_no_response', label: 'Stale no-response' },
];

function cx(...classes) {
  return classes.flat().filter(Boolean).join(' ');
}

export default function NeedsAttention() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState(getNeedsAttentionCounts());
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const reason = searchParams.get('reason') || '';

  const queueRows = useMemo(() => items.map(getNeedsAttentionRow), [items]);
  const emptyState = getNeedsAttentionEmptyState();

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const query = buildNeedsAttentionQuery({ reason, page: 1, limit: 25 });
      const data = await api.get(`/workflow/needs-attention?${query}`);
      setItems(data.items || []);
      setCounts(getNeedsAttentionCounts(data.counts || {}));
      setTotal(Number(data.total || data.counts?.total || 0));
      setPage(Number(data.page || 1));
      setLimit(Number(data.limit || 25));
    } catch (err) {
      setError('Needs-attention queue could not load. Refresh and try again.');
    } finally {
      setLoading(false);
    }
  }, [reason]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const updateReason = (nextReason) => {
    const next = new URLSearchParams(searchParams);
    if (nextReason) next.set('reason', nextReason);
    else next.delete('reason');
    setSearchParams(next);
  };

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
        <section className="mb-5 rounded-card border border-border-default bg-white p-5 shadow-card">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-warning-text">Dispatcher queue</p>
              <h2 className="mt-1 text-2xl font-bold tracking-[-0.02em] text-text-primary">Needs attention</h2>
              <p className="mt-1 max-w-2xl text-sm text-text-muted">Applicants where automation stopped or a dispatcher decision is needed.</p>
            </div>
            <Button as={Link} to="/" variant="secondary" size="sm">Back to applicants</Button>
          </div>
        </section>

        <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          {SUMMARY_CARDS.map(card => (
            <Card key={card.key} padding="compact" className="min-h-24">
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-text-muted">{card.label}</p>
              <p className="mt-2 text-3xl font-bold text-text-primary">{counts[card.key] || 0}</p>
            </Card>
          ))}
        </div>

        <Card className="mb-5" padding="compact">
          <div className="flex flex-wrap gap-2" aria-label="Needs-attention reason filters">
            {NEEDS_ATTENTION_FILTERS.map(filter => {
              const active = reason === filter.value;
              const count = filter.value ? counts[filter.value] : counts.total;
              return (
                <button
                  key={filter.label}
                  type="button"
                  onClick={() => updateReason(filter.value)}
                  className={cx(
                    'min-h-9 rounded-app border px-3 text-sm font-semibold transition-colors focus-ring',
                    active
                      ? 'border-warning-border bg-warning-bg text-warning-text'
                      : 'border-border-default bg-white text-text-secondary hover:bg-surface-hover hover:text-text-primary',
                  )}
                >
                  {filter.label}
                  {count > 0 && <span className="ml-1.5 text-xs opacity-75">{count}</span>}
                </button>
              );
            })}
          </div>
        </Card>

        {error && <Alert variant="danger" className="mb-4" role="alert">{error}</Alert>}

        <Card padding="compact" className="overflow-hidden p-0">
          {loading ? (
            <EmptyState title="Loading needs-attention queue..." description="Fetching applicants that need dispatcher review." icon="↻" />
          ) : queueRows.length === 0 ? (
            <EmptyState
              title={emptyState.title}
              description={emptyState.description}
              icon="!"
              action={<Button as={Link} to="/" variant="secondary">{emptyState.actionLabel}</Button>}
            />
          ) : (
            <>
              <div className="divide-y divide-border-default md:hidden">
                {queueRows.map(row => <NeedsAttentionCard key={`${row.reason.key}-${row.applicantId}`} row={row} />)}
              </div>
              <div className="hidden overflow-x-auto table-scrollbar md:block">
                <table className="w-full min-w-[920px] text-left">
                  <thead className="sticky top-0 bg-surface-subtle text-xs font-bold uppercase tracking-[0.04em] text-text-muted">
                    <tr className="border-b border-border-default">
                      <th className="px-4 py-3">Applicant</th>
                      <th className="px-4 py-3">Reason</th>
                      <th className="px-4 py-3">Stage</th>
                      <th className="px-4 py-3">Last touch</th>
                      <th className="px-4 py-3">Next action</th>
                      <th className="px-4 py-3">Age</th>
                      <th className="px-4 py-3">Open</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-default text-sm text-text-secondary">
                    {queueRows.map(row => <NeedsAttentionRow key={`${row.reason.key}-${row.applicantId}`} row={row} />)}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>

        {!loading && queueRows.length > 0 && (
          <p className="py-4 text-center text-sm text-text-muted" aria-live="polite">
            Showing {queueRows.length} of {total || queueRows.length} needs-attention item{(total || queueRows.length) === 1 ? '' : 's'}.
            {page > 1 || queueRows.length >= limit ? ' Use filters to narrow the queue.' : ''}
          </p>
        )}
      </div>
    </Layout>
  );
}

function NeedsAttentionRow({ row }) {
  return (
    <tr className="transition-colors hover:bg-surface-hover">
      <td className="px-4 py-3">
        <p className="font-semibold text-text-primary">{row.displayName}</p>
        <p className="text-xs text-text-muted">{row.sourceLabel}</p>
      </td>
      <td className="px-4 py-3">
        <Badge variant={row.reason.variant}>{row.reason.label}</Badge>
        <p className="mt-1 max-w-xs text-xs text-text-muted">{row.reason.helper}</p>
      </td>
      <td className="px-4 py-3">{row.stageLabel}</td>
      <td className="whitespace-nowrap px-4 py-3">{row.lastTouchLabel}</td>
      <td className="px-4 py-3 font-semibold text-brand-blue">{row.nextActionLabel}</td>
      <td className="whitespace-nowrap px-4 py-3">{row.ageLabel}</td>
      <td className="px-4 py-3">
        <Button as={Link} to={row.nextActionUrl} variant="secondary" size="sm">Open</Button>
      </td>
    </tr>
  );
}

function NeedsAttentionCard({ row }) {
  return (
    <article className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate font-semibold text-text-primary">{row.displayName}</h3>
          <p className="mt-0.5 text-xs text-text-muted">{row.stageLabel} · {row.sourceLabel}</p>
        </div>
        <Badge variant={row.reason.variant}>{row.reason.label}</Badge>
      </div>
      <p className="mt-3 text-sm text-text-secondary">{row.reason.helper}</p>
      <dl className="mt-3 grid grid-cols-2 gap-3 text-xs">
        <div>
          <dt className="font-semibold uppercase tracking-[0.08em] text-text-muted">Last touch</dt>
          <dd className="mt-1 text-text-primary">{row.lastTouchLabel}</dd>
        </div>
        <div>
          <dt className="font-semibold uppercase tracking-[0.08em] text-text-muted">Age</dt>
          <dd className="mt-1 text-text-primary">{row.ageLabel}</dd>
        </div>
        <div className="col-span-2">
          <dt className="font-semibold uppercase tracking-[0.08em] text-text-muted">Next action</dt>
          <dd className="mt-1 font-semibold text-brand-blue">{row.nextActionLabel}</dd>
        </div>
      </dl>
      <Button as={Link} to={row.nextActionUrl} variant="secondary" size="sm" className="mt-4 w-full">Open</Button>
    </article>
  );
}
